/**
 * Meddelande om ändring av hyresvillkor — tillägg till hyresavtal enligt
 * 12 kap. 54 § jordabalken (generisk variant).
 *
 * Two documents in one composition (ADR-0057): the meddelande, signed by the
 * hyresvärd, and the tillägg the hyresgäst(er) sign. Every party/property
 * datum is a slot value with an autofill binding; nothing is read from the
 * compile context except who signs.
 *
 * Legal text verbatim from `base-templates/Mall_meddelande om ändring av
 * hyresvillkor.docx` (Haymanot-approved). DO NOT paraphrase or reorder.
 */
import { group, p, section, spacer, t } from '@hyranai/regta-document-ir';
import { dateVal, optionVal, partySlot, propertySlot, sectionPlacement, selectSlot, signingBlockDefinition, textVal, } from '../common/index.js';
import { boxedRows, letterOpening } from './kit.js';
const SOURCE = 'base-templates/Mall_meddelande om ändring av hyresvillkor.docx';
const LEGAL = {
    plane: 'legal',
    status: 'published',
    language: 'sv-SE',
    legalReview: { reviewedBy: 'Haymanot', source: SOURCE },
};
/** "Antal rum och kök". `code` is the ADR-0062 code; the legacy lane stored (and printed) the label as `value`. */
export const APARTMENT_ROOMS_OPTIONS = [
    ['1 rum och kokskåp', 'r1_kokskap', '1 room with a kitchenette cupboard.'],
    ['1 rum och kokvrå', 'r1_kokvra', '1 room with a kitchen nook.'],
    ['1 rum och kök', 'r1_kok', '1 room with a full kitchen.'],
    ['1,5 rum och kök', 'r1_5_kok', '1.5 rooms with a full kitchen.'],
    ['2 rum och kokvrå', 'r2_kokvra', '2 rooms with a kitchen nook.'],
    ['2 rum och kök', 'r2_kok', '2 rooms with a full kitchen.'],
    ['2,5 rum och kök', 'r2_5_kok', '2.5 rooms with a full kitchen.'],
    ['3 rum och kök', 'r3_kok', '3 rooms with a full kitchen.'],
    ['3,5 rum och kök', 'r3_5_kok', '3.5 rooms with a full kitchen.'],
    ['4 rum och kök', 'r4_kok', '4 rooms with a full kitchen.'],
    ['4,5 rum och kök', 'r4_5_kok', '4.5 rooms with a full kitchen.'],
    ['5 rum och kök', 'r5_kok', '5 rooms with a full kitchen.'],
    ['6 rum och kök', 'r6_kok', '6 rooms with a full kitchen.'],
    ['7 rum och kök', 'r7_kok', '7 rooms with a full kitchen.'],
    ['8 rum och kök', 'r8_kok', '8 rooms with a full kitchen.'],
].map(([label, code, summary]) => ({ code: code, value: label, label: label, summary: summary }));
/** Party + property + contract slots of the InfoTable (legacy partyProjection + Lägenheten/Hyresavtalet). */
export const INFO_SLOTS = [
    partySlot('landlord_name', 'Namn', 'lessor', 0, 'name', { required: true }),
    partySlot('landlord_org_number', 'Personnr / Org.nr', 'lessor', 0, 'legalId'),
    partySlot('landlord_address', 'Adress', 'lessor', 0, 'address'),
    partySlot('tenant_name', 'Namn', 'lessee', 0, 'name', { required: true }),
    partySlot('tenant_personnummer', 'Personnr / Org.nr', 'lessee', 0, 'legalId', { help: 'PII — ej sökbart' }),
    partySlot('tenant_address', 'Adress', 'lessee', 0, 'address'),
    partySlot('cotenant_name', 'Namn (medhyresgäst)', 'lessee', 1, 'name'),
    partySlot('cotenant_personnummer', 'Personnr / Org.nr (medhyresgäst)', 'lessee', 1, 'legalId', { help: 'PII — ej sökbart' }),
    partySlot('cotenant_address', 'Adress (medhyresgäst)', 'lessee', 1, 'address'),
    propertySlot('property_designation', 'Fastighetsbeteckning', 'designation', { required: true }),
    propertySlot('property_address', 'Postadress', 'address', { type: 'textarea' }),
    { key: 'apartment_official_number', label: 'Lägenhetsnummer (officiell)', type: 'text' },
    {
        key: 'contract_number',
        label: 'Hyreskontrakt nr',
        type: 'text',
        required: true,
        help: 'Numret på det bostadshyresavtal som ändringen avser.',
    },
];
/** The InfoTable printed at the top of both the meddelande and the tillägg. */
export const uppgifter = {
    ...LEGAL,
    blockKey: 'andring_hyresvillkor.uppgifter',
    version: 'v1',
    title: 'Uppgifter om hyresavtalet',
    summary: 'Boxed table: landlord, contract number, property, tenant, address and apartment number.',
    slots: INFO_SLOTS,
    compile: (input) => [
        boxedRows(input, [
            ['Hyresvärd', 'landlord_name'],
            ['Hyreskontrakt nr', 'contract_number'],
            ['Fastighetsbeteckning', 'property_designation'],
            ['Hyresgäst', 'tenant_name'],
            ['Personnr / org.nr', 'tenant_personnummer'],
            ['Adress', 'tenant_address'],
            ['Postadress', 'property_address'],
            ['Lägenhetsnummer (officiell)', 'apartment_official_number'],
        ]),
    ],
};
/** "Härmed meddelas att undertecknad hyresvärd fr.o.m. … kommun." — shared by both ändring variants. */
export function noticeParagraph(input, ctx) {
    return p([
        t('Härmed meddelas att undertecknad hyresvärd fr.o.m. '),
        dateVal(ctx, input, 'effective_date'),
        t(' önskar ändra hyresvillkoren för din/er bostadslägenhet om '),
        optionVal(APARTMENT_ROOMS_OPTIONS, input, 'apartment_rooms'),
        t(' i fastigheten '),
        textVal(input, 'property_designation'),
        t(' i '),
        textVal(input, 'property_municipality'),
        t(' kommun.'),
    ]);
}
export function responseParagraph(input, ctx) {
    return p([
        t('Ett exemplar av tillägg till hyresavtalet önskas åter i undertecknat skick senast den '),
        dateVal(ctx, input, 'response_deadline'),
        t('. Om överenskommelse om de nya villkoren inte kan träffas, kommer ansökan om ändring av hyresvillkoren att lämnas in till hyresnämnden en månad efter det att detta meddelande lämnats till dig/er.'),
    ]);
}
export const NOTICE_SLOTS = [
    { key: 'effective_date', label: 'Ändringen träder i kraft fr.o.m.', type: 'date', required: true },
    selectSlot('apartment_rooms', 'Antal rum och kök', APARTMENT_ROOMS_OPTIONS, { required: true }),
    propertySlot('property_municipality', 'Kommun', 'municipality', { required: true }),
    {
        key: 'response_deadline',
        label: 'Svarsfrist',
        type: 'date',
        required: true,
        help: 'Datum då ett undertecknat exemplar av tillägget senast önskas åter.',
    },
];
const proposalBox = (input) => group([p([textVal(input, 'proposed_change_description', 'long')]), spacer('md')], { variant: 'boxed' });
export const rubrik = {
    ...LEGAL,
    blockKey: 'andring_hyresvillkor.rubrik',
    version: 'v1',
    title: 'Rubrik',
    summary: 'Opening of the meddelande: object type, title and statute.',
    slots: [],
    compile: () => letterOpening({
        kicker: 'Bostadslägenhet',
        title: 'Meddelande om ändring av hyresvillkor — tillägg till hyresavtal',
        subtitle: 'enligt 12 kap. 54 § jordabalken',
    }),
};
export const meddelande = {
    ...LEGAL,
    blockKey: 'andring_hyresvillkor.meddelande',
    version: 'v1',
    title: 'Meddelande',
    summary: 'The landlord’s notice of the proposed change, the proposed tillägg text and the response deadline.',
    slots: [
        ...NOTICE_SLOTS,
        {
            key: 'changes_concerning',
            label: 'De ändrade villkoren avser',
            type: 'textarea',
            required: true,
            help: 'Kort beskrivning av vilka hyresvillkor som ändras (t.ex. "hyran för lägenheten", "uppvärmningskostnaden").',
        },
        {
            key: 'proposed_change_description',
            label: 'Beskrivning av föreslagen ändring',
            type: 'textarea',
            required: true,
            help: 'Beskriv kort vad som ska gälla som bifogat tillägg till hyresavtalet (t.ex. "Hyrestillägg om 250 kr/månad").',
        },
    ],
    compile: (input, ctx) => [
        section('meddelande', 'Meddelande', [
            noticeParagraph(input, ctx),
            p('Hyresvärden föreslår att följande villkor ska gälla som tillägg till hyresavtalet:'),
            proposalBox(input),
            p('Hyresavtalet föreslås fortsätta gälla i övrigt på oförändrade villkor.'),
            responseParagraph(input, ctx),
        ]),
    ],
};
/** Signing section; the composition supplies `ref`, `title`, numbering and the signing `parties`. */
export const underskrift = signingBlockDefinition({
    blockKey: 'andring_hyresvillkor.underskrift',
    title: 'Underskrift',
    legalReview: LEGAL.legalReview,
});
export const tillaggRubrik = {
    ...LEGAL,
    blockKey: 'andring_hyresvillkor.tillagg-rubrik',
    version: 'v1',
    title: 'Rubrik (tillägg)',
    summary: 'Opening of the tillägg document.',
    slots: [],
    compile: () => letterOpening({ kicker: 'Tillägg till hyresavtal' }),
};
/** "Undertecknade parter har denna dag träffat följande tillägg till hyreskontrakt nr …." */
export const tillaggIngress = {
    ...LEGAL,
    blockKey: 'andring_hyresvillkor.tillagg-ingress',
    version: 'v1',
    title: 'Ingress (tillägg)',
    summary: 'The tillägg’s opening sentence naming the lease it amends.',
    slots: [],
    compile: (input) => [
        p([
            t('Undertecknade parter har denna dag träffat följande tillägg till hyreskontrakt nr '),
            textVal(input, 'contract_number'),
            t('.'),
        ]),
    ],
};
export function effectiveFromParagraph(input, ctx) {
    return p([
        t('Hyresvillkoren gäller fr.o.m. '),
        dateVal(ctx, input, 'effective_date'),
        t('.'),
    ]);
}
export const andradeHyresvillkor = {
    ...LEGAL,
    blockKey: 'andring_hyresvillkor.andrade-hyresvillkor',
    version: 'v1',
    title: 'Ändrade hyresvillkor',
    summary: 'The agreed change, its effective date, and that the lease otherwise continues unchanged.',
    slots: [],
    compile: (input, ctx) => {
        const { ref, title, opts } = sectionPlacement(input, {
            ref: 'andrade-hyresvillkor',
            title: 'Ändrade hyresvillkor',
            keepTogether: false,
        });
        return [
            section(ref, title, [
                proposalBox(input),
                effectiveFromParagraph(input, ctx),
                p('Hyresavtalet föreslås fortsätta gälla i övrigt på oförändrade villkor.'),
            ], opts),
        ];
    },
};
export const ANDRING_HYRESVILLKOR_BLOCKS = [
    uppgifter,
    rubrik,
    meddelande,
    underskrift,
    tillaggRubrik,
    tillaggIngress,
    andradeHyresvillkor,
];
//# sourceMappingURL=blocks.js.map